import * as React from 'react';
import styled from 'styled-components';

import AddContactWizard, { ContactPrefill, findSimilarCompanies } from '../forms/AddContactWizard';
import { ModalDefaultDraggable } from '../molecules';
import { CompanyTableRow, createUser, getUsersCustom, UserType } from '../api/users';
import { CLIENT_TYPES_VALUES, USER_TYPES_VALUES } from '../constants/forms';
import { LeadRequester } from '../types';
import {
  formatLeadPhone,
  requesterCompanyValues,
  requesterIndividualValues,
} from '../utils/projectImportFormValues';

const isCompany = (contact: any) => Number(contact?.clientType) === CLIENT_TYPES_VALUES.COMPANY;

// Companies and individuals live in different tables, so ids can collide.
const contactKey = (contact: any) => `${isCompany(contact) ? 'company' : 'user'}-${contact?.id}`;

const personName = (contact: any) => [contact?.firstName, contact?.lastName].filter(Boolean).join(' ');

/**
 * Shows the Monday lead's requester above a contacts picker and links it to
 * EPIC: an existing contact (matched by email) or company (matched by name)
 * can be added with one click; a missing one is created from the lead,
 * pre-filled, so the PM/PC doesn't retype it.
 *
 * `individualsOnly` is for project contacts, which can only be people: there
 * the company is only picked (or created) as the person's parent company.
 */
const LeadRequesterCard = ({
  requester, contacts, setContacts, individualsOnly = false,
}: Props) => {
  const companyName = (requester.company || '').trim();
  const email = (requester.email || '').trim().toLowerCase();

  const [isLoading, setIsLoading] = React.useState(true);
  const [existingContact, setExistingContact] = React.useState<any>(null);
  const [existingCompanies, setExistingCompanies] = React.useState<CompanyTableRow[]>([]);
  // Company picked or created here; the new individual goes under it.
  const [parentCompany, setParentCompany] = React.useState<CompanyTableRow | null>(null);
  const [prefill, setPrefill] = React.useState<ContactPrefill | null>(null);
  const [createErrors, setCreateErrors] = React.useState(null);

  const lookup = React.useCallback(async () => {
    setIsLoading(true);
    const [contactResult, companyResult] = await Promise.all([
      email
        ? getUsersCustom({
          userRole: USER_TYPES_VALUES.CLIENT,
          clientType: CLIENT_TYPES_VALUES.INDIVIDUAL,
          project: 'true',
          search: email,
        } as any).catch(() => null)
        : null,
      companyName ? findSimilarCompanies(companyName).catch(() => []) : [],
    ]);
    // get-all-users/ returns a plain array despite the UsersList typing.
    const people: any[] = (contactResult?.data as unknown as any[]) || [];
    setExistingContact(people.find(user => (user?.email || '').toLowerCase() === email) || null);
    setExistingCompanies(companyResult || []);
    setIsLoading(false);
  }, [email, companyName]);

  React.useEffect(() => {
    lookup();
  }, [lookup]);

  const isAdded = (contact: any) =>
    (contacts || []).some(c => contactKey(c) === contactKey(contact));

  const addContact = (contact: any) => {
    if (!isAdded(contact)) {
      setContacts([...(contacts || []), contact]);
    }
  };

  // Several name matches: use the one the reviewer picked; one match: that one.
  const companyForIndividual = parentCompany
    || (existingCompanies.length === 1 ? existingCompanies[0] : null);

  const pickCompany = (company: CompanyTableRow) => {
    setParentCompany(company);
    if (!individualsOnly) {
      addContact({ ...company, clientType: CLIENT_TYPES_VALUES.COMPANY });
    }
  };

  const isCompanyPicked = (company: CompanyTableRow) => (individualsOnly
    ? parentCompany?.id === company.id
    : isAdded({ ...company, clientType: CLIENT_TYPES_VALUES.COMPANY }));

  const openCreate = (startWith: ContactPrefill['startWith']) => {
    setCreateErrors(null);
    setPrefill({
      startWith,
      company: requesterCompanyValues(requester),
      individual: requesterIndividualValues(requester),
      parentCompany: startWith === 'individual' ? companyForIndividual : null,
    });
  };

  const handleCreated = (saved: any, sent: any) => {
    const contact = { ...sent, ...saved, clientType: Number(sent?.clientType) };
    if (isCompany(contact)) {
      setParentCompany(contact);
      if (individualsOnly) return;
    } else {
      // The contacts table shows companyName, and parentCompany here is a bare id.
      const parent = contact.parentCompany ? parentCompany || companyForIndividual : null;
      contact.companyName = saved?.companyName || parent?.companyName || '';
      delete contact.parentCompany;
    }
    addContact(contact);
  };

  // Individual "Save", or company "Save" without adding an individual.
  const handleSubmit = async (data: UserType) => {
    setCreateErrors(null);
    try {
      const res = await createUser(data);
      handleCreated(res.data, data);
      setPrefill(null);
      lookup();
    } catch (error) {
      setCreateErrors((error as any)?.response?.data || null);
    }
  };

  const contactLabel = existingContact
    && [personName(existingContact), existingContact.email].filter(Boolean).join(' · ');

  return (
    <Card>
      <Eyebrow>Requester from Monday</Eyebrow>
      <Details>
        <Detail>
          <DetailLabel>Company</DetailLabel>
          <DetailValue>{companyName || 'None (individual)'}</DetailValue>
        </Detail>
        <Detail>
          <DetailLabel>Contact</DetailLabel>
          <DetailValue>{requester.name || '—'}</DetailValue>
        </Detail>
        <Detail>
          <DetailLabel>Email</DetailLabel>
          <DetailValue>{requester.email || '—'}</DetailValue>
        </Detail>
        <Detail>
          <DetailLabel>Phone</DetailLabel>
          <DetailValue>{formatLeadPhone(requester.phone) || requester.phone || '—'}</DetailValue>
        </Detail>
        <Detail>
          <DetailLabel>Role</DetailLabel>
          <DetailValue>{requester.role || '—'}</DetailValue>
        </Detail>
      </Details>

      {isLoading ? (
        <Status>Checking EPIC for this company and contact…</Status>
      ) : (
        <Matches>
          {companyName && (
            <MatchRow>
              <MatchKind>Company</MatchKind>
              <MatchBody>
                {existingCompanies.length ? (
                  <>
                    <MatchNote>
                      {existingCompanies.length === 1
                        ? 'Already in EPIC:'
                        : 'Similar companies already in EPIC. Use the right one:'}
                    </MatchNote>
                    {existingCompanies.map(company => (
                      <MatchItem key={company.id}>
                        <MatchName>
                          {[company.companyName, company.email].filter(Boolean).join(' · ')}
                        </MatchName>
                        {isCompanyPicked(company) ? (
                          <AddedTag>{individualsOnly ? 'Selected' : 'Added'}</AddedTag>
                        ) : (
                          <ActionButton type="button" onClick={() => pickCompany(company)}>
                            Use
                          </ActionButton>
                        )}
                      </MatchItem>
                    ))}
                    <LinkButton type="button" onClick={() => openCreate('company')}>
                      It&apos;s a different company: create from lead
                    </LinkButton>
                  </>
                ) : (
                  <MatchItem>
                    <MatchNote>Not in EPIC yet.</MatchNote>
                    <ActionButton type="button" $primary onClick={() => openCreate('company')}>
                      Create from lead
                    </ActionButton>
                  </MatchItem>
                )}
              </MatchBody>
            </MatchRow>
          )}
          <MatchRow>
            <MatchKind>Contact</MatchKind>
            <MatchBody>
              {existingContact ? (
                <>
                  <MatchNote>Already in EPIC (same email):</MatchNote>
                  <MatchItem>
                    <MatchName>{contactLabel}</MatchName>
                    {isAdded(existingContact) ? (
                      <AddedTag>Added</AddedTag>
                    ) : (
                      <ActionButton type="button" onClick={() => addContact(existingContact)}>
                        Use
                      </ActionButton>
                    )}
                  </MatchItem>
                </>
              ) : (
                <MatchItem>
                  <MatchNote>
                    {email ? 'Not in EPIC yet.' : 'No email on the lead to match by.'}
                    {companyName && !companyForIndividual && existingCompanies.length > 1
                      && ' Pick the company above first to link them.'}
                  </MatchNote>
                  <ActionButton type="button" $primary onClick={() => openCreate('individual')}>
                    Create from lead
                  </ActionButton>
                </MatchItem>
              )}
            </MatchBody>
          </MatchRow>
          {individualsOnly && companyName && (
            <Hint>
              Project contacts are people, so the company is linked as their parent company.
              You can add the company itself to the permit on the next step.
            </Hint>
          )}
        </Matches>
      )}

      {prefill && (
        <ModalDefaultDraggable
          title={prefill.startWith === 'company' ? 'Add Company' : 'Add Individual'}
          onClose={() => setPrefill(null)}
          wide
          zIndex={20}
        >
          <AddContactWizard
            prefill={prefill}
            modalError={!!createErrors}
            initialErrors={createErrors}
            onSubmit={handleSubmit}
            onCompanyCreated={company => {
              handleCreated(company, company);
              lookup();
            }}
          />
        </ModalDefaultDraggable>
      )}
    </Card>
  );
};

const Card = styled.div`
  padding: 20px;
  margin-bottom: 20px;
  border: 1px solid var(--border);
  border-radius: 10px;
  background: var(--neutral-50);
  box-sizing: border-box;
  font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, 'Helvetica Neue',
    Arial, Roboto, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol';
`;

const Eyebrow = styled.div`
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--neutral-500);
  margin-bottom: 12px;
`;

const Details = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 12px 24px;
`;

const Detail = styled.div`
  min-width: 0;
`;

const DetailLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--neutral-500);
`;

const DetailValue = styled.div`
  font-size: 0.875rem;
  color: var(--mainText);
  overflow-wrap: anywhere;
`;

const Hint = styled.div`
  font-size: 0.8125rem;
  color: var(--neutral-500);
`;

const Status = styled.div`
  margin-top: 16px;
  font-size: 0.875rem;
  color: var(--neutral-500);
`;

const Matches = styled.div`
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid var(--border);
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const MatchRow = styled.div`
  display: flex;
  gap: 16px;
  @media (max-width: 640px) {
    flex-direction: column;
    gap: 4px;
  }
`;

const MatchKind = styled.div`
  flex: 0 0 72px;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--mainText);
  line-height: 32px;
`;

const MatchBody = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const MatchItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 32px;
`;

const MatchNote = styled.div`
  font-size: 0.875rem;
  color: var(--neutral-600);
`;

const MatchName = styled.div`
  font-size: 0.875rem;
  color: var(--mainText);
  min-width: 0;
  overflow-wrap: anywhere;
`;

const ActionButton = styled.button<{ $primary?: boolean }>`
  flex-shrink: 0;
  height: 32px;
  padding: 0 14px;
  border-radius: 8px;
  font-size: 0.8125rem;
  font-weight: 600;
  text-transform: none;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease;
  border: 1px solid ${({ $primary }) => ($primary ? 'var(--primary-600)' : 'var(--border)')};
  background: ${({ $primary }) => ($primary ? 'var(--primary-600)' : 'var(--neutral-0)')};
  color: ${({ $primary }) => ($primary ? 'var(--neutral-0)' : 'var(--neutral-700)')};

  &:hover {
    background: ${({ $primary }) => ($primary ? 'var(--primary-700)' : 'var(--neutral-50)')};
    border-color: ${({ $primary }) => ($primary ? 'var(--primary-700)' : 'var(--neutral-400)')};
  }
  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;

const AddedTag = styled.span`
  flex-shrink: 0;
  padding: 4px 10px;
  border-radius: 999px;
  border: 1px solid var(--primary-100);
  background: var(--primary-50);
  color: var(--primary-700);
  font-size: 0.75rem;
  font-weight: 600;
`;

const LinkButton = styled.button`
  align-self: flex-start;
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  font-size: 0.8125rem;
  color: var(--primary-600);
  text-decoration: underline;
  cursor: pointer;

  &:hover {
    color: var(--primary-700);
  }
`;

interface Props {
  requester: LeadRequester;
  contacts: any[] | null;
  setContacts: (contacts: any[]) => void;
  individualsOnly?: boolean;
}

export default LeadRequesterCard;
